import {
	PlayHqFixtureGame,
	PlayHqGameScores,
	fetchPlayHqGameCentreScores,
	fetchPlayHqSpectatorGameScores,
	isCompletedPlayHqStatus,
	makeCumulativePlayHqResult,
} from './playHqApi'
import { makeFinalOnlyResult } from './fixtureSync'
import { hasDetailedPeriodScores, isValidResult } from './resultValidation'

const CACHE_KEY = 'playHqScoreChecks'
const MAX_ENTRIES = 1500
const RETRY_MS = 6 * 60 * 60 * 1000
const RECENT_GAME_MS = 14 * 24 * 60 * 60 * 1000

type CachedScore = {
	fingerprint: string
	localResultKey: string
	result: Result | null
	checkedAt: number
	retryAt: number | null
}

// Cache proposals locally so reopening the preview doesn't repeat historical lookups.
// Manual refresh always retries; Skip game remains the way to exclude a game entirely.
export async function fetchPlayHqResult(
	game: PlayHqFixtureGame,
	tenant: string,
	numPeriods: number,
	mode: FixtureSyncMode = 'quick',
	localResult?: Result,
): Promise<Result | null> {
	const key = JSON.stringify([ tenant, game.id, game.amHome ])
	const fingerprint = JSON.stringify([ 1, numPeriods, game.date, game.status, game.meScore, game.themScore ])
	const localResultKey = resultKey(localResult)
	const entry = readCache()[key]
	const cached = entry?.fingerprint === fingerprint &&
		(entry.result === null || isValidResult(entry.result, numPeriods)) &&
		(entry.localResultKey === localResultKey || (entry.result !== null && resultKey(entry.result) === localResultKey))
		? entry : undefined

	const remember = (result: Result | null, retryAt: number | null) => {
		saveCachedScore(key, { fingerprint, localResultKey, result, checkedAt: Date.now(), retryAt })
		return result
	}

	const fixtureResult = detailedResult(game, numPeriods)
	if (fixtureResult) return remember(fixtureResult, null)
	if (mode === 'quick' && cached && (cached.retryAt === null || Date.now() < cached.retryAt)) {
		// Recognize acceptance on the next check, without storing import state in Firestore.
		if (cached.localResultKey !== localResultKey) remember(cached.result, cached.retryAt)
		return cached.result
	}

	let fetchedScores: PlayHqGameScores = game
	let failed = false
	let lookupConflictsWithCache = false
	for (const fetchScores of [ fetchPlayHqGameCentreScores, fetchPlayHqSpectatorGameScores ]) {
		try {
			const scores = await fetchScores(game, tenant)
			if (scores) {
				if (cached?.result) {
					lookupConflictsWithCache ||= (
						(scores.meScore != null && scores.meScore !== cached.result.me[numPeriods - 1]) ||
						(scores.themScore != null && scores.themScore !== cached.result.them[numPeriods - 1])
					)
				}
				fetchedScores = mergeFinalScores(fetchedScores, scores)
				// Validate quarters against the totals from that same response.
				const result = detailedResult(scores, numPeriods)
				if (result) return remember(result, null)
			}
		} catch (err) {
			failed = true
			console.warn('Unable to check PlayHQ period scores', game.id, err)
		}
	}

	// Keep an unaccepted proposal during an outage, but never revive one after a local edit.
	// Only lookup totals can contradict it; missing totals may inherit stale fixture values.
	if (failed && cached?.result && !lookupConflictsWithCache) {
		return remember(cached.result, cached.retryAt === null ? null : Date.now() + RETRY_MS)
	}
	let result: Result | null = null
	if (
		!hasDetailedPeriodScores(localResult, numPeriods) &&
		isCompletedPlayHqStatus(fetchedScores.status) &&
		typeof fetchedScores.meScore === 'number' && Number.isFinite(fetchedScores.meScore) &&
		typeof fetchedScores.themScore === 'number' && Number.isFinite(fetchedScores.themScore)
	) {
		result = makeFinalOnlyResult(fetchedScores.meScore, fetchedScores.themScore, numPeriods)
		if (!isValidResult(result, numPeriods)) result = null
	}
	return remember(result, failed || Date.now() - game.date < RECENT_GAME_MS ? Date.now() + RETRY_MS : null)
}

function detailedResult(scores: PlayHqGameScores, numPeriods: number) {
	if (!Number.isFinite(scores.meScore) || !Number.isFinite(scores.themScore)) return null
	const result = makeCumulativePlayHqResult(scores, numPeriods)
	return result && isValidResult(result, numPeriods) ? result : null
}

function mergeFinalScores(base: PlayHqGameScores, scores: PlayHqGameScores): PlayHqGameScores {
	return {
		status: scores.status ?? base.status,
		meScore: scores.meScore ?? base.meScore,
		themScore: scores.themScore ?? base.themScore,
	}
}

function resultKey(result: Result | null | undefined) {
	return JSON.stringify(result ? [ result.me, result.them ] : null)
}

function readCache(): Record<string, CachedScore> {
	try {
		return JSON.parse(localStorage.getItem(CACHE_KEY) ?? '{}') ?? {}
	} catch {
		return {}
	}
}

function saveCachedScore(key: string, entry: CachedScore) {
	try {
		const others = Object.entries(readCache()).filter(([ id ]) => id !== key)
		others.sort(([, a ], [, b ]) => b.checkedAt - a.checkedAt)
		const entries = [ [ key, entry ], ...others ].slice(0, MAX_ENTRIES)
		localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(entries)))
	} catch (err) {
		console.warn('Unable to save cached PlayHQ scores', err)
	}
}
